



















































































































import { Component, Mixins, Watch } from 'vue-property-decorator';
import { Mutations, Actions, Buttons, MessageActionTypes } from '@/ts/store/Constants';
import { mapGetters } from 'vuex';
import ConversationData from '@/ts/ConversationData';
import MessageData from '@/ts/MessageData';
import RemoteImage from '@/components/Media/RemoteImage.vue';
import FullScreenImageViewer from '@/components/Media/FullScreenImageViewer.vue';
import DeviceMethods from '@/mixins/DeviceMethods.vue';
import PersonData from '@/ts/PersonData';
import DateMethods from '@/mixins/DateMethods.vue';

import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

@Component({
    components: {
        RemoteImage,
        FullScreenImageViewer,
    },
    computed: {
        ...mapGetters({
            visibleConversationsStatus: 'Messages/getVisibleConversationsStatus',
            activeConversation: 'Messages/activeConversation',
            hasNewImageData: 'Camera/hasNewImageData',
        }),
    },
})
export default class UserConversationChat extends Mixins(DeviceMethods, DateMethods) {
    public deviceInfo: any = null;
    private hasNewImageData: boolean;
    private textInputElement: HTMLTextAreaElement = null;
    private keyboardListenerAdded: boolean = false;
    private keyboardVisible: boolean = false;
    private fileData: File;

    constructor() {
        super();

        this.initializeKeyboardListener();
    }

    get activeConversation(): ConversationData {
        return this.$store.getters['Messages/activeConversation'];
    }

    public goToConvertTicket(conversationId: number) {
        this.$router.push({path: `/user/convert/${conversationId}`});
    }

    public beforeCreate() {
        this.$store.commit(Mutations.SET_LEFT_HEADER_BUTTON, Buttons.StaffBackButton);
        this.$store.commit(Mutations.SET_RIGHT_HEADER_BUTTON, Buttons.StaffUnitButton);
        this.$store.commit(Mutations.SET_STAFF_CHAT_BACK_LINK, true);
    }

    public created() {
        this.$store.commit(Mutations.SET_BOTTOM_MENU_VISIBILITY, false);
        this.$store.commit(Mutations.SET_HEADER_TEXT, this.conversation.shortParticipantNames(20));
    }

    public mounted() {
        this.textInputElement = (this.$refs.messageText as HTMLTextAreaElement);
        this.scrollToBottom();
    }

    get imageData(): string {
        return this.$store.getters['Camera/capturedImageData'];
    }

    get conversation(): ConversationData {
        return this.$store.getters['Messages/activeConversation'];
    }

    get dataLoaded(): boolean {
        return (
            this.conversation !== null
            && this.conversation.participants !== null
            && this.conversation.messages !== null
        );
    }

    get previewStyles(): string {
        if (!this.imageData) {
            return '';
        }

        const width = this.$store.state.Viewport.width * 0.30;
        const ratio = this.$store.state.Camera.metaData.width / this.$store.state.Camera.metaData.height;
        const height = width * (1.0 / ratio);

        let styleString = 'width: ' + width + 'px; ';
        styleString += 'height: ' + height + 'px; ';
        styleString += 'top: -' + (height + 20) + 'px;';

        return styleString;
    }

    get messageList(): MessageData[] {
        const clone = this.conversation.messageList.slice(0);

        return clone.sort((firstMessage, secondMessage) => {
            if (firstMessage.sortingCriterion > secondMessage.sortingCriterion) {
                return 1;
            }
            if (firstMessage.sortingCriterion < secondMessage.sortingCriterion) {
                return -1;
            }
            return 0;
        });
    }

    get user(): PersonData {
        return this.$store.getters['User/getUser'];
    }

    get currentMessage(): string {
        return this.$store.getters['Messages/getCurrentMessage'];
    }

    set currentMessage(value: string) {
        return;
    }

    get sendButtonClasses(): object {
        return {
            'hococo-inactive': !(this.readyToSend),
        };
    }

    get readyToSend(): boolean {
        return (this.conversation.participants.length > 1
            && (this.currentMessage.length > 0 || this.hasNewImageData)
            && !this.$store.getters['Messages/isWaitingForResponse']
        );
    }

    public changeConversationStatus(status: string) {
        this.$store.dispatch(Actions.CHANGE_CONVERSATION_STATUS, {status , conversationId: this.activeConversation.id, type: 'janitor'});
    }

    public isMessageByMe(message: MessageData) {
        return message.authorId === this.user.id;
    }

    public initializeKeyboardListener(): void {
        if (this.keyboardListenerAdded === true) {
            return;
        }

        if (this.$store.state.Viewport.deviceInfo.platform === 'web') {
            this.keyboardListenerAdded = true;
            return;
        }

        Keyboard.addListener('keyboardDidShow', (info: KeyboardInfo) => {
            this.keyboardVisible = true;
            this.scrollToBottom();
        });

        Keyboard.addListener('keyboardDidHide', () => {
            this.keyboardVisible = false;
        });

        this.keyboardListenerAdded = true;
    }

    public fullScreenImageViewer(guid: string) {
        this.$store.commit(Mutations.ADD_ACTIVE_FULLSCREEN_IMAGE, guid);
        this.$store.commit(Mutations.IS_FULLSCREEN, true);
    }

    public sender(message: MessageData) {
        return this.$store.getters['User/getCacheUser'](message.authorId);
    }

    public messageTime(message: MessageData) {
        const time    = this.$options.filters.moment(message.time, 'HH.mm');
        const isToday = message.time.isSame(Date.now(), 'day');

        return (isToday === true)
            ? `${this.$t('general.today', 'Today')} ${time}`
            : this.$options.filters.moment(message.time, 'ddd HH.mm');
    }

    public actionMessage(message: MessageData) {
        if (!message || message.actionType === null) {
            return '';
        }

        const author = this.$store.getters['User/getCacheUser'](message.authorId);
        const target = this.$store.getters['User/getCacheUser'](message.actionTarget);

        switch (message.actionType) {
            case MessageActionTypes.ADD_PARTICIPANT:
                return `${author.firstName} ${ this.$t('general.added', 'added')} ${target.firstName} ${ this.$t('general.to-the-conversation', 'to the conversation')}`;

            case MessageActionTypes.REMOVE_PARTICIPANT:
                return `${author.firstName} ${ this.$t('general.removed', 'removed')} ${target.firstName} ${ this.$t('general.from-the-conversation', 'from the conversation')}`;

            default:
                return '';
        }
    }

    public handleSendEvent(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.sendMessage();
    }

    public sendMessage(): void {
        if (this.readyToSend) {
            this.$store.dispatch(Actions.SEND_MESSAGE)
                .then(() => {
                    this.resetInputField();
                });
        }
    }

    public resetInputField() {
        this.textInputElement.value = '';
    }

    public ignoreEvent(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    public handleMessageInput(event: KeyboardEvent): void {
        this.$store.commit(Mutations.UPDATE_CURRENT_MESSAGE, this.textInputElement.value);
    }

    public showCameraDialog(): void {
        this.device().then((deviceInfo) => {
            if (deviceInfo.platform === 'web') {
                this.$store.commit(Mutations.SET_PHOTO_RECIPIENT, 'messages');
                this.$store.dispatch(Actions.SHOW_CAMERA_DIALOG);
            } else {
                this.$store.commit(Mutations.SET_PHOTO_RECIPIENT, 'messages');
                this.requestMobileImage();
            }
        });
    }

    public handleFileChange(event: Event) {
        this.fileData = (event.target as HTMLInputElement).files[0];
        this.$store.dispatch(Actions.UPLOAD_ATTACHMENT, this.fileData);
    }

    public scrollToBottom() {
        setTimeout(() => {
            const feedContainer = (this.$refs.messagesFeed as HTMLElement);
            feedContainer.scrollTop = feedContainer.scrollHeight - feedContainer.clientHeight;
        }, 0);
    }

    private dismissTakenImage(): void {
        this.$store.commit(Mutations.RESET_CAPTURED_IMAGE_DATA);
    }

    private async requestMobileImage(): Promise<boolean> {
        const image = await Camera.getPhoto({
            quality: 90,
            allowEditing: false,
            correctOrientation: true,
            resultType: CameraResultType.DataUrl,
            source: CameraSource.Prompt,
        }).then((photo) => {
            let tempImage = new Image();
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            tempImage.onload = () => {
                const width = 1024;
                const widthProportion = width / tempImage.width;
                const height = tempImage.height * widthProportion;

                canvas.width = width;
                canvas.height = height;
                context.drawImage(tempImage, 0, 0, width, height);

                tempImage = null;

                this.$store.commit(Mutations.SAVE_CAPTURED_IMAGE_DATA, {
                    meta: {
                        width,
                        height,
                    },
                    data: canvas.toDataURL('image/jpeg'),
                });
            };

            tempImage.src = photo.dataUrl;
        });

        return true;
    }

    @Watch('messageList')
    private messageListWatcher() {
        this.scrollToBottom();
    }
}
